import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Tables, TablesInsert } from "@/integrations/supabase/types";
import { useSupabaseAuth } from '@/contexts/SupabaseAuthContext';
import { getLostItems, createLostItem as createItem, updateLostItem, deleteLostItem } from '@/services/supabaseLostFound';
import { toast } from '@/components/ui/sonner';
import { 
  normalizeLostItems, 
  getLostItemWithUser, 
  getLostItemsArray,
  selectLostItemById 
} from '@/lib/utils';

export function useLostItems() {
  const queryClient = useQueryClient();
  const { user } = useSupabaseAuth();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['lost_items'],
    queryFn: async () => {
      const data = await getLostItems();
      // Filter out deleted items past 48h
      const now = Date.now();
      const filtered = data.filter(item => {
        if (item.status === 'Deleted') {
          const expKey = `lostitem_delete_${item.id}`;
          const expiry = localStorage.getItem(expKey);
          if (!expiry || now >= Number(expiry)) {
            return false; // Hide item
          }
        }
        return true;
      });
      // Map location and room_number for frontend, and cast status to Status
      const mapped = filtered.map(item => ({
        ...item,
        location: (item as any).location,
        room_number: (item as any).room_number,
        // Cast status to Status to satisfy LostItem typing
        status: item.status as import("@/types").Status,
      }));
      // No need to generate signed URLs; use the public URL directly
      return mapped;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: true,
    retry: 2,
  });

  const createMutation = useMutation({
    mutationFn: (item: Omit<Partial<TablesInsert<'lost_items'>>, 'id'>) => {
        return createItem(item, user);
    },
    onSuccess: () => {
        toast.success("Lost item created successfully");
        queryClient.invalidateQueries({ queryKey: ['lost_items'] })
    },
    onError: (error: Error) => {
        toast.error(`Creation Failed: ${error.message}`);
    }
  });

  // Normalized data (for new components)
  const normalizedItems = data ? normalizeLostItems(data) : {};

  // Helper functions for normalized data access
  const getItemById = (itemId: number) => selectLostItemById(itemId, normalizedItems);
  const getItemWithUserData = (itemId: number, users: Record<string, Tables<'profiles'>>) => 
    getLostItemWithUser(itemId, normalizedItems, users);

  return { 
    lostItems: data, // array format (backward compatible)
    itemsNormalized: normalizedItems, // normalized format
    isLoading, 
    error, 
    getItemById,
    getItemWithUserData,
    getItemsArray: () => getLostItemsArray(normalizedItems),
    createLostItem: createMutation.mutateAsync, 
    refetchLostItems: refetch 
  };
}

export function useUpdateLostItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: number, changes: Partial<Tables<'lost_items'>> }) => {
        return updateLostItem(vars);
    },
    onSuccess: () => {
        toast.success("Lost item updated successfully");
        queryClient.invalidateQueries({ queryKey: ['lost_items'] })
    },
    onError: (error: Error) => {
        toast.error(`Update Failed: ${error.message}`);
    }
  });
}

export function useDeleteLostItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => {
        return deleteLostItem(id);
    },
    onSuccess: () => {
        toast.success("Lost item deleted successfully");
        queryClient.invalidateQueries({ queryKey: ['lost_items'] })
    },
    onError: (error: Error) => {
        toast.error(`Deletion Failed: ${error.message}`);
    }
  });
}
